import SendIcon from '@mui/icons-material/Send';
import { Box, SxProps } from '@mui/material';
import { makeStyles } from '@mui/styles';
import { MarkdownInput } from '@react-admin/ra-markdown';
import isFunction from 'lodash/isFunction';
import { useCallback, useRef, useState } from 'react';
import { Create, RaRecord, Toolbar, useRecordContext, useRefresh } from 'react-admin';
import { useFormState } from 'react-hook-form';

import { SaveButton } from '@sh4res/bo-common/components';
import { CommentsProps } from '@sh4res/bo-common/components/Comments/CommentsButton';
import { SimpleForm } from '@sh4res/bo-common/components/SimpleForm';
import { useExpandButton } from '@sh4res/bo-common/hooks';

import type { Editor, EditorOptions } from '@toast-ui/editor';

type ToolbarItemOptions = NonNullable<EditorOptions['toolbarItems']>[number][number];

const commentDefaultStyle: SxProps = {
  display: 'flex',
  height: '250px',
  width: '100%',
  maxWidth: '500px',
};

const commentExpandedStyle: SxProps = {
  display: 'flex',
  height: 'calc(100vh - 80px)',
  width: 'calc(100vw - 50px)',
  position: 'fixed',
  top: '60px',
  left: '20px',
  zIndex: '100',
};

export function CommentsCreate({
  target,
  getFilterValue,
  source,
}: Pick<CommentsProps, 'target' | 'getFilterValue' | 'source'>) {
  const refresh = useRefresh();
  const editor = useRef<Editor | undefined>();
  const [editorExpanded, setEditorExpanded] = useState<boolean>(false);
  const toggleExpand = useCallback(() => setEditorExpanded(!editorExpanded), [editorExpanded]);
  const { expandButton } = useExpandButton({ onClick: toggleExpand });
  const record = useRecordContext<RaRecord>();

  const effectiveTarget = isFunction(target) ? target(record) : target;

  const effectiveRecordId = getFilterValue ? getFilterValue(record?.[source]) : record?.[source];

  if (!record) {
    return null;
  }

  return (
    <Create
      resource="bo_comments"
      // to not render title in appbar
      title=" "
      component="div"
      redirect={false}
      mutationOptions={{
        onSuccess: () => {
          refresh();
          // Markdown input is not reset on submit
          // Could not find a better way
          if (editor.current) {
            editor.current.setHTML('');
          }
        },
      }}
      transform={(data: RaRecord) => ({
        comment: data.comment,
        [effectiveTarget]: effectiveRecordId,
      })}
      sx={{
        '& .RaCreate-main': {
          margin: 0,
        },
        '& .toastui-editor-defaultUI-toolbar .toastui-editor-toolbar-group': { flexWrap: 'wrap' },
        '& .toastui-editor-popup-add-link': {
          position: 'fixed !important',
          left: 'auto !important',
          right: '30px !important',
          top: '130px !important',
        },
      }}>
      <SimpleForm
        component={FormComponent}
        toolbar={<CustomToolbar />}
        mode="onBlur"
        defaultValues={{ comment: null }}>
        <Box sx={editorExpanded ? commentExpandedStyle : commentDefaultStyle}>
          <MarkdownInput
            data-testid="BoExternalCommentsCreate_CommentInput"
            source="comment"
            label={false}
            height="100%"
            autofocus={false}
            plugins={[
              (context) => {
                editor.current = context.instance;
                return {};
              },
            ]}
            // https://nhn.github.io/tui.editor/latest/tutorial-example15-customizing-toolbar-buttons
            toolbarItems={[
              [
                [
                  {
                    name: 'Resize',
                    tooltip: 'Resize',
                    el: expandButton,
                  },
                ],
                ['heading', 'bold', 'italic', 'strike'],
                ['link'],
                ['ul', 'ol', 'task'],
                ['hr', 'quote'],
              ].flatMap((item) => item as unknown as ToolbarItemOptions),
            ]} // groups are not supported for some reason, so we need to flatten all options
          />
        </Box>
      </SimpleForm>
    </Create>
  );
}

function CustomToolbar() {
  const classes = useStyles();
  const { isDirty } = useFormState();
  return (
    <Toolbar className={classes.toolbar}>
      <SaveButton
        label="general.comments.send"
        disabled={!isDirty}
        permissions={{ resource: 'bo_comments', action: 'create' }}
        icon={<SendIcon />}
        testId="BoExternalCommentsCreate_SendButton"
      />
    </Toolbar>
  );
}

const useStyles = makeStyles((theme) => ({
  toolbar: {
    minHeight: theme.spacing(6),
    marginBottom: 0,
    // move the toolbar closer to the input
    marginTop: '-9px',
    // material-ui uses !important, so we need to use it too to overwrite the value
    justifyContent: 'center !important',
  },
}));

// eslint-disable-next-line @typescript-eslint/no-explicit-any
const FormComponent = (props: any) => <div {...props} />;
